import { SelectOption, useNotify } from '@globaleris/ui.library'
import { Button } from '@material-ui/core'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumTipoDocumento, EnumTipoPeaje } from '../../../../api'
import { DocumentModel } from '../../../../api/Argentia/endpoints/documents/model/document'
import { LeadModel } from '../../../../api/Argentia/endpoints/leads/model/leads'
import { EnumNivelDesglose } from '../../../../api/Argentia/enums/enumNivelDesglose'
import { EnumProrrateo } from '../../../../api/Argentia/enums/enumProrrateo'
import Page, { PageLayout } from '../../../../components/page/page'
import PageCenterColumn from '../../../../components/page/pageCenterColumn'
import PageHeader from '../../../../components/page/pageHeader'
import PageLeftColumn from '../../../../components/page/pageLeftColumn'
import {
  AttachFileDialog,
  AttachFileDialogRef
} from '../../../../components/shared/documents/attachFileDialog'
import { ObjectionsDialog } from '../../../../components/shared/objectionsDialog/objectionsDialog'
import { HistoricalFilesCard } from '../../../../components/shared/profiles/historicalFilesCard/historicalFilesCard'
import { enumToSelectOptionArrayDefaultValues, enumTranslateOption } from '../../../../helpers'
import { fileDownloadHelper } from '../../../../helpers/fileDownloadHelper'
import { getUserPermissions } from '../../../../helpers/permissionsHelper'
import { NonAuthRoutes } from '../../../../model'
import { Pages } from '../../../../model/RBAC/constants'
import {
  completeDataTableActions,
  mapDocumentsModelToUploadFileModel
} from '../../../documents/documents.mapper'
import {
  downloadDocument,
  downloadLeadProfileTemplate,
  downloadProfileSelectionTemplate,
  fetchDocumentObjectionsDescriptions,
  fetchObjectionsByDocumentId,
  getDocumentsByLeadId,
  uploadFile
} from '../../../documents/documents.service'
import { ObjectionTableModel } from '../../../invoicesPage/invoicesPage.Model'
import { mapAttachedDocumentFileItemToDocument } from '../../../procedures/procedures.mapper'
import { AttachedDocumentFile } from '../../../procedures/procedures.types'
import { getLeadById } from '../../leads.service'
import { LeadsProfileDetailLeftPanel } from './components/lead.profiles.detail.leftPanel'

interface LeadsProfileDetailPageProps {
  location?: {
    state: {
      leadId?: number
    }
  }
}

export const LeadsProfileDetailPage: React.FC<LeadsProfileDetailPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'leads', 'documents', 'profiles'])
  const history = useHistory()
  const { info, error } = useNotify()

  const attachFileDialogRef = useRef<AttachFileDialogRef>(null)
  const [showAttachmentDialog, setShowAttachmentDialog] = useState<boolean>(false)
  const [attachFileDialogTitle, setAttachFileDialogTitle] = useState<string>('')
  const [documentsTypes, setDocumentsTypes] = useState<SelectOption[]>(
    enumToSelectOptionArrayDefaultValues(EnumTipoDocumento, 'enums:EnumTipoDocumento', [
      EnumTipoDocumento.MeasureTemplate
    ])
  )
  const [documentData, setDocumentData] = useState<DocumentModel[]>([])
  const [leadData, setLeadData] = useState<LeadModel>({} as LeadModel)
  const [breakdownLevel, setbreakdownLevel] = useState<EnumNivelDesglose | undefined>(undefined)
  const [profileSelectionTool, setProfileSelectionTool] = useState<boolean>(false)
  const [documentObjections, setDocumentObjections] = useState<ObjectionTableModel[] | undefined>(
    undefined
  )
  const [documentObjectionsDescription, setDocumentObjectionsDescription] = useState<
    ObjectionTableModel[] | undefined
  >(undefined)
  const [objectionsDialogIsOpen, setObjectionsDialogIsOpen] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isFetchingDocumentData, setIsFetchingDocumentData] = useState<boolean>(false)

  const leadId = props.location?.state?.leadId

  if (!leadId) {
    history.goBack()
    return <></>
  }
  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  // ------------------- Historic Files/Document Card Handlers -----------------------------//

  const handleDownloadFile = async (document: DocumentModel) => {
    if (document.id) {
      await downloadDocument(document.id).then(response => {
        if (response.success && response.data) {
          fileDownloadHelper(response.data.name, response.data)
        } else {
          error(t('documents:notifications.downloadFiles.error'))
        }
      })
    }
  }

  const handleViewObjections = async (document: DocumentModel) => {
    setObjectionsDialogIsOpen(true)
    await fetchObjectionsByDocumentId(document.id!).then(response => {
      if (response.success) {
        setDocumentObjections(response.data)
      }
    })
  }

  const handleConsumptionByProfile = () => {
    setProfileSelectionTool(false)
    setbreakdownLevel(EnumNivelDesglose.PeriodoTemporal)
    setAttachFileDialogTitle(t('profiles:components.historicalFilesCard.dialogTitle.profile'))
    handleOpenDialog()
  }

  const handleConsumptionByCurve = () => {
    setProfileSelectionTool(false)
    setbreakdownLevel(undefined)
    setAttachFileDialogTitle(t('profiles:components.historicalFilesCard.dialogTitle.curve'))
    handleOpenDialog()
  }

  const handleConsumptionByPeriod = () => {
    setProfileSelectionTool(false)
    setbreakdownLevel(EnumNivelDesglose.PeridoTemporalTarifario)
    setAttachFileDialogTitle(t('profiles:components.historicalFilesCard.dialogTitle.period'))
    handleOpenDialog()
  }

  const handleConsumptionWithProfileSelection = () => {
    setProfileSelectionTool(true)
    setbreakdownLevel(undefined)
    setAttachFileDialogTitle(
      t('profiles:components.historicalFilesCard.dialogTitle.profileSelection')
    )
    handleOpenDialog()
  }

  // -------------------- Objections Actions -------------------- //
  const handleObjectionsDialogClose = () => {
    setObjectionsDialogIsOpen(false)
    setDocumentObjections(undefined)
  }

  // -------------------- Attach File Dialog Handlers -------------------- //

  const handleCloseDialog = () => {
    onResetDialog()
    setShowAttachmentDialog(false)
  }

  const handleOpenDialog = () => {
    setShowAttachmentDialog(true)
  }

  const handleSaveDocument = async (document: AttachedDocumentFile, reopen?: boolean) => {
    await mapAttachedDocumentFileItemToDocument(document).then(async document => {
      const request = mapDocumentsModelToUploadFileModel([document], document.type)
      return await uploadFile(request)
        .then(response => {
          if (response.success) {
            info(t('documents:notifications.create.success'))
            fetchDocumentData()
          } else {
            error(t('documents:notifications.create.error'))
          }
        })
        .finally(() => {})
    })
    handleCloseDialog()
    reopen &&
      setTimeout(() => {
        handleOpenDialog()
      }, 700)
  }

  const handleSaveAndNewDocument = (document: AttachedDocumentFile) => {
    handleSaveDocument(document, true)
  }

  const onResetDialog = () => {
    attachFileDialogRef?.current && attachFileDialogRef?.current?.reset()
  }

  const handleDownloadTemplate = async () => {
    if (profileSelectionTool) {
      await downloadProfileSelectionTemplate(leadData.id!).then(response => {
        if (response.success && response.data) {
          fileDownloadHelper(response.data.name, response.data)
        } else {
          error(t('leads:fetchErrors.downloadTemplate'))
        }
      })
    } else {
      await downloadLeadProfileTemplate(leadData.id!, breakdownLevel).then(response => {
        if (response.success && response.data) {
          fileDownloadHelper(response.data.name, response.data)
        } else {
          error(t('leads:fetchErrors.downloadTemplate'))
        }
      })
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const fetchDocumentData = useCallback(async () => {
    setIsFetchingDocumentData(true)
    await getDocumentsByLeadId(leadId)
      .then(response => {
        if (response.success && response.data) {
          setDocumentData(completeDataTableActions(response.data))
        } else {
          error(t('leads:fetchErrors.documentData'))
        }
      })
      .finally(() => {
        setIsFetchingDocumentData(false)
      })
  }, [leadId])

  const fetchLeadData = useCallback(async () => {
    setIsFetching(true)
    await getLeadById(leadId)
      .then(response => {
        if (response.success && response.data) {
          setLeadData(response.data)
          fetchDocumentData()
        } else {
          error(t('leads:fetchErrors.fetchLeadData'))
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [leadId])

  useEffect(() => {
    fetchLeadData()
    fetchDocumentData()
  }, [])

  useEffect(() => {
    fetchDocumentObjectionsDescriptions().then(response => {
      if (response.success) {
        setDocumentObjectionsDescription(response.data)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.MULTI_COLUMN} fullWidth={true}>
      <PageLeftColumn>
        <LeadsProfileDetailLeftPanel
          isLoading={isFetching}
          code={leadData.code ?? ''}
          description={leadData.description ?? ''}
          toll={
            leadData.tollType &&
            enumTranslateOption(leadData.tollType, EnumTipoPeaje, 'enums:EnumTipoPeaje')
          }
          breakdownLevel={
            leadData.breakdownLevel &&
            enumTranslateOption(
              leadData.breakdownLevel,
              EnumNivelDesglose,
              'enums:EnumNivelDesglose'
            )
          }
          weightedType={
            leadData.weightedType &&
            enumTranslateOption(leadData.weightedType, EnumProrrateo, 'enums:EnumProrrateo')
          }
          commodity={enumTranslateOption(leadData.commodity, EnumCommodity, 'enums:EnumCommodity')}
          profileCode={leadData.profile?.code ?? ''}
          productCode={leadData.product?.code ?? ''}
        />
      </PageLeftColumn>

      <PageCenterColumn>
        <PageHeader title={t('leads:components.leadsProfileDetail.header.title')}>
          <Button variant="text" color="primary" size="small" onClick={handleBackButtonClick}>
            {t('leads:components.leadsProfileDetail.header.actions.back')}
          </Button>
        </PageHeader>

        <HistoricalFilesCard
          data={documentData}
          isLoading={isFetchingDocumentData}
          onDownloadActionHandler={handleDownloadFile}
          onViewObjectionsHandler={handleViewObjections}
          onConsumptionByProfileClick={handleConsumptionByProfile}
          onConsumptionByCurveClick={handleConsumptionByCurve}
          onConsumptionByPeriodClick={
            leadData.commodity === EnumCommodity.Electricidad
              ? handleConsumptionByPeriod
              : undefined
          }
          onConsumptionWithProfileSelectionClick={
            leadData.commodity === EnumCommodity.Electricidad
              ? handleConsumptionWithProfileSelection
              : undefined
          }
          showDownloadCurrentButton
          supplyPointId={leadData.supplyPoint?.id}
        />
      </PageCenterColumn>

      <ObjectionsDialog
        open={objectionsDialogIsOpen}
        handleClose={handleObjectionsDialogClose}
        title={t('profiles:components.objectionsDialog.title')}
        objDescriptions={documentObjectionsDescription ?? []}
        objectionsData={documentObjections}
        hasEditPermission={false}
        tableUserPermissions={getUserPermissions(Pages.Leads)}
      />

      <AttachFileDialog
        prolifeFormConfig
        ref={attachFileDialogRef}
        open={showAttachmentDialog}
        documentTypes={documentsTypes}
        onClose={handleCloseDialog}
        onSave={handleSaveDocument}
        onSaveAndNew={handleSaveAndNewDocument}
        ondownloadTemplate={handleDownloadTemplate}
        customTitle={attachFileDialogTitle}
      />
    </Page>
  )
}
